import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, IndianRupee, Printer, Receipt, Search, Share2, Trash2, Users } from "lucide-react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import StatCard from "../components/common/StatCard";
import { erpApi, formatCurrency, formatDate, normalizeExternalUrl } from "../services/erpApi";

const reportMessage = (sample) =>
  `Hello ${sample.patient_name}, your ${sample.test_name} report from ${sample.lab_name || "ATDC"} is ready.\nPDF: ${normalizeExternalUrl(sample.report_pdf_url)}`;

export default function Reports() {
  const [summary, setSummary] = useState({});
  const [bills, setBills] = useState([]);
  const [samples, setSamples] = useState([]);
  const [billSearch, setBillSearch] = useState("");
  const [paymentBill, setPaymentBill] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: "", payment_mode: "cash", notes: "" });

  const load = async () => {
    const [summaryRes, billsRes, samplesRes] = await Promise.all([erpApi.dashboard(), erpApi.bills(), erpApi.samples()]);
    setSummary(summaryRes.data);
    setBills(billsRes.data);
    setSamples(samplesRes.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load reports"));
  }, []);

  const filteredBills = useMemo(() => {
    const term = billSearch.trim().toLowerCase();
    if (!term) return bills;

    return bills.filter((bill) =>
      [
        bill.invoice_no,
        bill.id,
        bill.patient_id,
        bill.patient_name,
        bill.patient_mobile,
        bill.created_at,
        bill.status,
        bill.total,
        bill.paid_amount,
        bill.due_amount,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [billSearch, bills]);

  const exportCsv = async () => {
    const response = await API.get("/reports/export", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `billing_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadBillPdf = async (bill) => {
    try {
      const response = await erpApi.billPdf(bill.id);
      const file = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");

      link.href = url;
      const fileName = String(bill.invoice_no || bill.id.slice(0, 8)).replace(/[^a-z0-9_-]+/gi, "_");
      link.download = `invoice_${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF download failed");
    }
  };

  const deleteBill = async (bill) => {
    const ok = window.confirm(`Delete invoice #${bill.id.slice(0, 8).toUpperCase()} for ${bill.patient_name}?`);
    if (!ok) return;
    try {
      await erpApi.deleteBill(bill.id);
      toast.success("Bill deleted");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bill");
    }
  };

  const openPayment = (bill) => {
    setPaymentBill(bill);
    setPaymentForm({ amount: String(bill.due_amount || ""), payment_mode: bill.payment_mode || "cash", notes: "" });
  };

  const collectPayment = async (event) => {
    event.preventDefault();
    if (!paymentBill || !paymentForm.amount) return toast.error("Payment amount required");
    try {
      await erpApi.addBillPayment(paymentBill.id, { ...paymentForm, amount: Number(paymentForm.amount) });
      toast.success("Payment recorded");
      setPaymentBill(null);
      setPaymentForm({ amount: "", payment_mode: "cash", notes: "" });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to collect payment");
    }
  };

  const shareReport = (sample) => {
    const reportUrl = normalizeExternalUrl(sample.report_pdf_url);
    if (!reportUrl) return toast.error("Report PDF URL missing");
    const phone = String(sample.patient_mobile || "").replace(/\D/g, "");
    const whatsappNumber = phone.length === 10 ? `91${phone}` : phone;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(reportMessage(sample))}`, "_blank", "noopener,noreferrer");
  };

  const openReport = (url) => {
    const reportUrl = normalizeExternalUrl(url);
    if (!reportUrl) return toast.error("Report PDF URL missing");
    window.open(reportUrl, "_blank", "noopener,noreferrer");
  };

  const markDelivered = async (sample) => {
    await erpApi.updateSample(sample.id, { status: "delivered" });
    toast.success("Report marked delivered");
    load();
  };

  const reportRows = samples.filter((sample) => sample.report_pdf_url || ["report_received", "printed", "delivered"].includes(sample.status));

  return (
    <div className="space-y-6">
      <div className="flex justify-between gap-4">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Reports</h1>
          <p className="text-sm text-hospital-text-muted">Lab report inbox, patient sharing, billing history and exports.</p>
        </div>
        <Button className="btn-primary" onClick={exportCsv}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard title="Today's Revenue" value={formatCurrency(summary.total_revenue_today)} icon={IndianRupee} />
        <StatCard title="Patients Today" value={summary.patients_today || 0} icon={Users} />
        <StatCard title="Bills Today" value={summary.total_bills_today || 0} icon={Receipt} />
        <StatCard title="Pending Reports" value={summary.pending_reports || 0} icon={Download} tone="warning" to="/samples" />
      </div>

      <DataTable
        title="Lab Reports Inbox"
        rows={reportRows}
        empty="No lab reports received yet"
        columns={[
          { key: "patient_name", label: "Patient", render: (row) => <Link className="font-medium text-hospital-primary" to={`/patients/${row.patient_id}`}>{row.patient_name}</Link> },
          { key: "patient_mobile", label: "Mobile" },
          { key: "test_name", label: "Test" },
          { key: "lab_name", label: "Lab" },
          { key: "status", label: "Status", render: (row) => <span className="capitalize">{row.status?.replaceAll("_", " ")}</span> },
          { key: "expected_date", label: "Date", render: (row) => row.expected_date || formatDate(row.collection_date) },
          {
            key: "actions",
            label: "Actions",
            render: (row) => (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" title="Open PDF" onClick={() => openReport(row.report_pdf_url)}>
                  <FileText className="h-4 w-4 text-hospital-primary" />
                </Button>
                <Button variant="ghost" size="icon" title="Print PDF" onClick={() => openReport(row.report_pdf_url)}>
                  <Printer className="h-4 w-4 text-slate-700" />
                </Button>
                <Button variant="ghost" size="icon" title="Share on WhatsApp" onClick={() => shareReport(row)}>
                  <Share2 className="h-4 w-4 text-emerald-700" />
                </Button>
                {row.status !== "delivered" && (
                  <Button variant="outline" size="sm" onClick={() => markDelivered(row)}>
                    Delivered
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <DataTable
        title={`All Bills (${filteredBills.length})`}
        rows={filteredBills}
        empty="No bills"
        actions={
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              className="pl-10"
              placeholder="Search name, mobile, patient ID, bill no..."
              value={billSearch}
              onChange={(event) => setBillSearch(event.target.value)}
            />
          </div>
        }
        columns={[
          { key: "invoice_no", label: "Invoice", render: (row) => row.invoice_no || `#${row.id.slice(0, 8).toUpperCase()}` },
          { key: "created_at", label: "Date", render: (row) => formatDate(row.created_at) },
          { key: "patient_name", label: "Patient", render: (row) => <Link className="font-medium text-hospital-primary" to={`/patients/${row.patient_id}`}>{row.patient_name}</Link> },
          { key: "status", label: "Status", render: (row) => <span className="capitalize">{row.status}</span> },
          { key: "total", label: "Total", align: "right", render: (row) => formatCurrency(row.total) },
          { key: "paid_amount", label: "Paid", align: "right", render: (row) => formatCurrency(row.paid_amount) },
          { key: "due_amount", label: "Due", align: "right", render: (row) => formatCurrency(row.due_amount) },
          {
            key: "actions",
            label: "",
            render: (row) => (
              <div className="flex items-center justify-end gap-1">
                {Number(row.due_amount || 0) > 0 && row.status !== "draft" && (
                  <Button variant="outline" size="sm" onClick={() => openPayment(row)}>
                    Collect
                  </Button>
                )}
                {row.invoice_pdf_url && (
                  <Button variant="ghost" size="icon" title="Open imported bill PDF" onClick={() => openReport(row.invoice_pdf_url)}>
                    <FileText className="h-4 w-4 text-hospital-primary" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" title="Download invoice PDF" onClick={() => downloadBillPdf(row)}>
                  <Download className="h-4 w-4 text-hospital-primary" />
                </Button>
                <Button variant="ghost" size="icon" title="Delete bill" onClick={() => deleteBill(row)}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]}
      />
      <Dialog open={Boolean(paymentBill)} onOpenChange={(open) => !open && setPaymentBill(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Collect Due Payment</DialogTitle></DialogHeader>
          <form onSubmit={collectPayment} className="space-y-3">
            <div className="rounded-lg bg-hospital-primary-light p-3 text-sm">
              <div className="flex justify-between"><span>Invoice</span><strong>{paymentBill?.invoice_no || paymentBill?.id?.slice(0, 8)}</strong></div>
              <div className="flex justify-between"><span>Patient</span><strong>{paymentBill?.patient_name}</strong></div>
              <div className="flex justify-between"><span>Due</span><strong>{formatCurrency(paymentBill?.due_amount)}</strong></div>
            </div>
            <div className="space-y-2"><Label>Amount Received</Label><Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} /></div>
            <div className="space-y-2"><Label>Payment Mode</Label><Select value={paymentForm.payment_mode} onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_mode: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="bank">Bank</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Notes</Label><Input value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setPaymentBill(null)}>Cancel</Button><Button className="btn-primary">Save Payment</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
